import moment from "moment";
import type { AppDispatch } from "src/redux/store";
import { POST_MESSAGE_EVENTS, STORAGE_KEYS } from "src/constants/common";
import storage from "src/helpers/storage";
import {
  glucoseDataReceived,
  connected,
  disconnected,
  statusUpdate,
  sensorStateChanged,
  cgmError,
  cgmHardSyncSuccess,
  cgmHardSyncError,
  cgmGetLatestSuccess,
  cgmGetLatestError,
  cgmMissingSyncSuccess,
  cgmMissingSyncError,
  cgmAndroidBackgroundSyncStatus,
  bumpCgmRemoteRefreshNonce,
  getDailyLogsRequest,
  getCgmStatusRequest,
  cgmBluetoothRadioSnapshot,
} from "src/redux/slices/cgmSlice";
import type { PocGlucoseJson, PocDeviceJson } from "src/types/cgm.types";
import type { ParsedCgmMessage } from "./parseCgmMessage";
import { normalizeCgmHardSyncPayload } from "./normalizeCgmHardSyncPayload";
import { persistLastReadingSyncTimestampFromHardSyncPayload } from "./persistCgmLastReadingSyncTimestamp";

/**
 * Handle a parsed CGM message by dispatching to Redux.
 * Returns true if the message was handled, false otherwise.
 * Used by CgmMessageListener and (Phase 2) cgmManager.startLiveDataListener.
 */
export function handleCgmMessage(
  payload: ParsedCgmMessage | null,
  dispatch: AppDispatch
): boolean {
  if (!payload) return false;
  const { action, data } = payload;

  if (action === POST_MESSAGE_EVENTS.CGM_GLUCOSE_DATA && data?.readings) {
    dispatch(
      glucoseDataReceived({
        readings: data.readings as PocGlucoseJson[],
        batteryPercent: data.batteryPercent as number | null | undefined,
        connectionStatus: data.connectionStatus as string | null | undefined,
        sensorState: data.sensorState as number | null | undefined,
        sensorStateName: data.sensorStateName as string | null | undefined,
        isConnected: data.isConnected as boolean | undefined,
        boundDevice: data.boundDevice as PocDeviceJson | null | undefined,
        warmupEndDate: data.warmupEndDate as string | null | undefined,
      })
    );
    return true;
  }
  if (action === POST_MESSAGE_EVENTS.CGM_CONNECTED && data?.pocDevice) {
    dispatch(connected(data.pocDevice as PocDeviceJson));
    return true;
  }
  if (action === POST_MESSAGE_EVENTS.CGM_DISCONNECTED) {
    dispatch(disconnected());
    return true;
  }
  if (
    action === POST_MESSAGE_EVENTS.CGM_SENSOR_STATE &&
    data?.sensorState != null
  ) {
    const connectionStatus =
      (data.connectionStatus as string | null | undefined) ??
      (data.connStatus as string | null | undefined) ??
      null;
    const sensorState = data.sensorState as number;
    dispatch(
      sensorStateChanged({
        sensorState,
        statusName: data.statusName as string | undefined,
        connectionStatus,
        readings: data.readings as PocGlucoseJson[] | undefined,
        boundDevice: data.boundDevice as PocDeviceJson | null | undefined,
        warmupEndDate: data.warmupEndDate as string | null | undefined,
        isConnected: data.isConnected as boolean | undefined,
      })
    );
    if (sensorState === 113 || sensorState === 114) {
      dispatch(getCgmStatusRequest());
    }
    return true;
  }
  if (action === POST_MESSAGE_EVENTS.CGM_STATUS_UPDATE && data) {
    const initEndDate = data.initEndDate as string | null | undefined;
    if (initEndDate && (data.status as string) === "warmup") {
      storage.setItem(STORAGE_KEYS.CGM_WARMUP_END_DATE, initEndDate);
    }
    const status = data.status as string;
    dispatch(
      statusUpdate({
        status,
        pocDevice: data.pocDevice as PocDeviceJson | null | undefined,
        initEndDate,
      })
    );
    if (status === "unbound" || status === "no_device") {
      dispatch(getCgmStatusRequest());
    }
    return true;
  }
  if (action === POST_MESSAGE_EVENTS.CGM_ERROR) {
    dispatch(cgmError((data?.error as string) ?? "Unknown error"));
    return true;
  }

  if (action === POST_MESSAGE_EVENTS.CGM_HARD_SYNC_SUCCESS) {
    persistLastReadingSyncTimestampFromHardSyncPayload(data);
    dispatch(
      cgmHardSyncSuccess({
        result: normalizeCgmHardSyncPayload(data),
      })
    );
    dispatch(
      getDailyLogsRequest({
        startDate: moment().format("YYYY-MM-DD"),
      })
    );
    return true;
  }

  if (action === POST_MESSAGE_EVENTS.CGM_HARD_SYNC_ERROR) {
    dispatch(
      cgmHardSyncError({ error: (data?.error as string) ?? "Sync failed" })
    );
    return true;
  }

  if (action === POST_MESSAGE_EVENTS.CGM_GET_LATEST_READING_SUCCESS) {
    persistLastReadingSyncTimestampFromHardSyncPayload(data);
    dispatch(cgmGetLatestSuccess());
    dispatch(bumpCgmRemoteRefreshNonce());
    dispatch(
      getDailyLogsRequest({
        startDate: moment().format("YYYY-MM-DD"),
      })
    );
    return true;
  }

  if (action === POST_MESSAGE_EVENTS.CGM_GET_LATEST_READING_ERROR) {
    dispatch(
      cgmGetLatestError({
        error: (data?.error as string) ?? "Failed to sync latest reading",
      })
    );
    return true;
  }

  if (action === POST_MESSAGE_EVENTS.CGM_SYNC_MISSING_POINTS_SUCCESS) {
    persistLastReadingSyncTimestampFromHardSyncPayload(data);
    dispatch(cgmMissingSyncSuccess());
    dispatch(bumpCgmRemoteRefreshNonce());
    dispatch(
      getDailyLogsRequest({
        startDate: moment().format("YYYY-MM-DD"),
      })
    );
    return true;
  }

  if (action === POST_MESSAGE_EVENTS.CGM_SYNC_MISSING_POINTS_ERROR) {
    dispatch(
      cgmMissingSyncError({
        error: (data?.error as string) ?? "Missing points sync failed",
      })
    );
    return true;
  }

  if (action === POST_MESSAGE_EVENTS.CGM_ANDROID_BACKGROUND_SYNC_STATUS) {
    const running = Boolean(data?.running);
    const source =
      typeof data?.source === "string" ? (data.source as string) : undefined;
    dispatch(cgmAndroidBackgroundSyncStatus({ running, source }));
    return true;
  }

  /**
   * RN host adapter events (AppScreen → WebView) — align Redux with `/cgm/connection` when
   * Tracky does not emit sensorState (e.g. Bluetooth toggled off).
   */
  if (action === POST_MESSAGE_EVENTS.BLUETOOTH_OFF) {
    dispatch(cgmBluetoothRadioSnapshot({ radio: "off" }));
    dispatch(getCgmStatusRequest());
    return true;
  }
  if (action === POST_MESSAGE_EVENTS.BLUETOOTH_PERMISSION_DENIED) {
    dispatch(cgmBluetoothRadioSnapshot({ radio: "denied" }));
    dispatch(getCgmStatusRequest());
    return true;
  }
  if (action === POST_MESSAGE_EVENTS.BLUETOOTH_ON) {
    dispatch(cgmBluetoothRadioSnapshot({ radio: "on" }));
    dispatch(getCgmStatusRequest());
    return true;
  }

  return false;
}
